const e = encodeURIComponent;

/** Returns `/tenant/{id}` prefix when tenantId is provided, empty string otherwise. */
export function tenantPrefix(tenantId?: string | null): string {
  return tenantId ? `/tenant/${e(tenantId)}` : '';
}

/** Build a location-scoped path: /location/:location/projects/...rest */
export function buildPath(location: string, ...rest: string[]): string {
  return `/location/${e(location)}/projects${rest.length ? '/' + rest.join('/') : ''}`;
}

/**
 * Typed path builders — location-scoped routes with optional tenant prefix.
 * When `tid` (tenantId) is provided, URLs are prefixed with `/tenant/{tid}`.
 * Location always uses `/location/{region}` prefix for unambiguous parsing.
 */
export const paths = {
  /** Dashboard home — shows projects tab */
  projects: () => '/',
  /** Dashboard with region filter pre-selected (tenant-prefixed when tenantId provided). */
  locationProjects: (loc: string, tid?: string | null) => {
    // No tenant prefix → URL is `/?tab=...` (root + query). With a tenant
    // prefix the URL must be `/tenant/{tid}?tab=...` — not
    // `/tenant/{tid}/?tab=...` — because the React Router config matches
    // `/tenant/:tenantId` without a trailing slash. The previous form
    // inserted the slash unconditionally, which broke tenant-scoped
    // dashboard navigation under strict routers.
    const prefix = tenantPrefix(tid);
    const base = prefix || '/';
    return `${base}?tab=projects&region=${e(loc)}`;
  },
  projectApps: (loc: string, project: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps`,
  projectSettings: (loc: string, project: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/settings`,
  /**
   * Project Settings page deep-linked to a specific tab via `?tab=<tab>`.
   * Used e.g. by the free-grant banner to jump straight to the Free grants tab.
   */
  projectSettingsTab: (loc: string, project: string, tab: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/settings?tab=${e(tab)}`,
  projectKnowledge: (loc: string, project: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/knowledge`,
  projectSandboxes: (loc: string, project: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/sandboxes`,
  appWorkflows: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/workflows`,
  appWorkflowDesigner: (loc: string, project: string, app: string, wf: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/workflows/${e(wf)}`,
  appConnections: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/connections`,
  appParameters: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/parameters`,
  appSettings: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/settings`,
  appAnalytics: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/analytics`,
  appAgents: (loc: string, project: string, app: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/agents`,
  appAgentDetail: (loc: string, project: string, app: string, agent: string, tid?: string | null) =>
    `${tenantPrefix(tid)}/location/${e(loc)}/projects/${e(project)}/apps/${e(app)}/agents/${e(agent)}`,
} as const;

/**
 * @deprecated Use `paths` builders instead. These route templates are
 * string constants for backward compat — prefer the typed builders above.
 */
export const routes = {
  projects: '/?tab=projects',
  projectApps: '/location/:locationName/projects/:projectName/apps',
  projectSettings: '/location/:locationName/projects/:projectName/settings',
  projectKnowledge: '/location/:locationName/projects/:projectName/knowledge',
  projectSandboxes: '/location/:locationName/projects/:projectName/sandboxes',
  appWorkflows: '/location/:locationName/projects/:projectName/apps/:appName/workflows',
  appWorkflowDesigner: '/location/:locationName/projects/:projectName/apps/:appName/workflows/:workflowName',
  appConnections: '/location/:locationName/projects/:projectName/apps/:appName/connections',
  appParameters: '/location/:locationName/projects/:projectName/apps/:appName/parameters',
  appAnalytics: '/location/:locationName/projects/:projectName/apps/:appName/analytics',
  appSettings: '/location/:locationName/projects/:projectName/apps/:appName/settings',
  appAgents: '/location/:locationName/projects/:projectName/apps/:appName/agents',
  appAgentDetail: '/location/:locationName/projects/:projectName/apps/:appName/agents/:agentName',
  login: '/login',
  /** @deprecated Use paths.projects() */
  home: '/?tab=projects',
} as const;

export type RoutePath = (typeof routes)[keyof typeof routes];
